import { useEffect, useMemo, useState } from 'react';
import commonStore from '../../../../store/common';
import { observer } from 'mobx-react-lite';
import authStore from '../../../../store/auth';
import { useForm } from 'react-hook-form';
import { reportingInstitutions, ACTION_TYPES } from './consts';
import { httpRequest } from '../../../../config/api';
import { Alert, Drawer, Box } from '@mantine/core';
import { TbInfoCircle } from 'react-icons/tb';
import { Jurisdictions } from './Jurisdictions';
import { Funds } from './Funds';
import { cloneDeep } from 'lodash';
import toast from 'react-hot-toast';
import { AdditionalInformationForm } from './AdditionalInformationForm';
import { JurisdictionsActions } from './JurisdictionsActions';
import { DrawerTitle } from './DrawerTitle';
import { useExportParams } from '../../../../services/report.service';

function base64ToBlob(base64, contentType = 'application/zip') {
  const sliceSize = 512;
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
}

function downloadBlob(blob, filename) {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

function downloadZipMap(zipMap) {
  Object.keys(zipMap).forEach(key => {
    const blob = base64ToBlob(zipMap[key]);
    downloadBlob(blob, `${key}.zip`);
  });
}

export const ExportDrawer = observer(
  ({ id, jurisdictions: jurisdictionsData, periodId, workflowId, hasWarning = false }) => {
    const jurisdictions = useMemo(() => {
      if (!jurisdictionsData || jurisdictionsData.length === 0) return [];

      return jurisdictionsData.map(item => ({
        ...item,
        funds: item.national_codes
          ?.filter(item => item?.is_included)
          ?.map(item => ({
            ...(item || {}),
            id: item?.fund_id,
            name: item?.fund_name
          }))
      }));
    }, [jurisdictionsData]);

    const [isLoading, setIsLoading] = useState(false);
    const [exportWithoutEntries, setExportWithoutEntries] = useState(false);
    const [showSenderIdNotice, setShowSenderIdNotice] = useState(false);
    const token = authStore.token;
    const [selectedJurisdictions, setSelectedJurisdictions] = useState([]);
    const [expandedCountryCodes, setExpandedCountryCodes] = useState([]);
    const [action, setAction] = useState({
      type: ACTION_TYPES.JURISDICTIONS
    });

    const { data: exportParams } = useExportParams({
      id: periodId,
      queryParams: { enabled: !!periodId && commonStore.isOpenExport }
    });

    const [isMergeAll, setIsMergeAll] = useState(true);
    const { control, handleSubmit, watch, reset } = useForm();
    const onClose = () => {
      commonStore.setIsOpenExport(false);
      setSelectedJurisdictions([]);
      setIsMergeAll(true);
      setShowSenderIdNotice(false);
    };

    const jurisdictionsIds = useMemo(() => {
      return selectedJurisdictions.filter(item => item.xml).map(item => item.reportingMemberState);
    }, [selectedJurisdictions]);

    useEffect(() => {
      if (commonStore.isOpenExport) {
        setSelectedJurisdictions(
          jurisdictions.map(item => ({
            reportingMemberState: item.reporting_member_state,
            pdf: true,
            xml: true,
            funds: item.funds.map(item => item.id)
          }))
        );
        setExpandedCountryCodes(jurisdictions.map(item => item.reporting_member_state));
      }
    }, [commonStore.isOpenExport]);

    const onConfirm = async data => {
      if (jurisdictionsIds.length > 0) {
        const values = { ...data };

        if (exportWithoutEntries) {
          if (jurisdictionsIds.includes('FI')) {
            values['typeofreportinginstitution_aifm'] = '';
            values['typeofreportinginstitution_aif'] = '';
            values['reportingentityId'] = '';
            values['typeofId'] = '';
          }
          if (jurisdictionsIds.includes('LU')) {
            values['sender_id'] = '';
          }
        }

        try {
          setIsLoading(true);

          const funds_by_jurisdiction = {};
          const _jusrisdictions = selectedJurisdictions.filter(item => item.xml);

          _jusrisdictions.forEach(item => {
            funds_by_jurisdiction[item.reportingMemberState] = item.funds;
          });

          const url = `${import.meta.env.VITE_BASE_URL}api/report/create_xml_zip/${periodId}/?zip_combine=${isMergeAll}`;
          const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            workflow_id: workflowId
          };

          const requestData = {
            funds_by_jurisdiction,
            ...values
          };

          const requestOptions = isMergeAll ? { headers, responseType: 'blob' } : { headers };
          const response = await httpRequest.post(url, requestData, requestOptions);

          if (!isMergeAll) {
            downloadZipMap(response);
          } else {
            downloadBlob(response, 'Report XML.zip');
          }

          reset();
        } catch (err) {
          if (err?.status === 403) {
            toast.error('Access forbidden: this workflow is not assigned to you');
          } else {
            toast.error('Something went wrong');
          }
        } finally {
          setIsLoading(false);
        }
      }

      const jurisdictionIdsPDF = selectedJurisdictions.filter(item => item.pdf);

      if (jurisdictionIdsPDF.length > 0) {
        try {
          setIsLoading(true);
          const url = `${import.meta.env.VITE_BASE_URL}api/generate-pdf-report?period_id=${periodId}`;
          const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          };

          const requestData = selectedJurisdictions
            .filter(item => item.pdf)
            .map(item => ({
              jurisdiction: item.reportingMemberState,
              fund_ids: item.funds
            }));

          const response = await httpRequest.post(url, requestData, {
            headers,
            responseType: 'blob'
          });

          downloadBlob(response, 'Report PDF.zip');
        } catch (e) {
          toast.error('Error generating PDF file');
        } finally {
          setIsLoading(false);
        }
      }

      onBack();
    };

    const onSkip = () => {
      if (
        jurisdictionsIds.includes('LU') ||
        jurisdictionsIds.includes('FI') ||
        jurisdictionsIds.includes('DK')
      ) {
        setAction({
          type: ACTION_TYPES.ADDITIONAL_INFORMATION
        });
        setShowSenderIdNotice(jurisdictionsIds.includes('LU') && !exportWithoutEntries);
        reset({
          typeofreportinginstitution_aif: exportParams?.typeofreportinginstitution_aif,
          typeofreportinginstitution_aifm: exportParams?.typeofreportinginstitution_aifm,
          idno: exportParams?.idno,
          refperio: exportParams?.refperio,
          loebenr: exportParams?.loebenr,
          firmanavn: exportParams?.firmanavn,
          gadenavn: exportParams?.gadenavn,
          postnr: exportParams?.postnr,
          by: exportParams?.by,
          fuldtnavn: exportParams?.fuldtnavn,
          tlf_kontaktperson: exportParams?.tlf_kontaktperson,
          email: exportParams?.email,
          reportingentityId: exportParams?.reportingentityId,
          typeofId: exportParams?.typeofId,
          sender_id: exportParams?.sender_id
        });
      } else {
        onConfirm();
      }
    };

    const typeOfIds = [
      {
        value: 'Y',
        label: 'Y'
      },
      {
        value: 'TK',
        label: 'TK'
      },
      {
        value: 'LEI',
        label: 'LEI'
      }
    ];

    const onSubmit = data => {
      const values = {};
      Object.keys(data).forEach(key => {
        if (data[key] !== undefined) {
          values[key] = data[key];
        }
      });
      onConfirm(data);
    };

    const isDisabledExport = useMemo(() => {
      if (jurisdictionsIds.includes('DK')) {
        if (
          watch('idno') &&
          watch('refperio') &&
          watch('loebenr') &&
          watch('fuldtnavn') &&
          watch('tlf_kontaktperson') &&
          watch('email')
        ) {
          return false;
        } else {
          return true;
        }
      }

      if (exportWithoutEntries) {
        return false;
      }

      if (
        jurisdictionsIds.includes('FI') &&
        jurisdictionsIds.includes('LU') &&
        !exportWithoutEntries
      ) {
        if (
          watch('typeofreportinginstitution_aifm') &&
          watch('typeofreportinginstitution_aif') &&
          watch('reportingentityId') &&
          watch('typeofId') &&
          watch('sender_id')
        ) {
          return false;
        } else {
          return true;
        }
      }

      if (jurisdictionsIds.includes('FI') && !exportWithoutEntries) {
        if (
          watch('typeofreportinginstitution_aifm') &&
          watch('typeofreportinginstitution_aif') &&
          watch('reportingentityId') &&
          watch('typeofId')
        ) {
          return false;
        } else {
          return true;
        }
      }

      if (jurisdictionsIds.includes('LU') && !exportWithoutEntries) {
        if (watch('sender_id')) {
          return false;
        } else {
          return true;
        }
      }

      return false;
    }, [watch(), exportWithoutEntries]);

    const onSelectAllJurisdictions = () => {
      if (selectedJurisdictions.length === jurisdictions.length) {
        setSelectedJurisdictions([]);
      } else {
        setSelectedJurisdictions(
          jurisdictions.map(item => ({
            reportingMemberState: item.reporting_member_state,
            pdf: false,
            xml: false,
            funds: item.funds.map(item => item.id)
          }))
        );
      }
    };

    const onSelectJurisdiction = reportingMemberState => {
      if (selectedJurisdictions.find(item => item.reportingMemberState === reportingMemberState)) {
        setSelectedJurisdictions(prev =>
          prev.filter(item => item.reportingMemberState !== reportingMemberState)
        );
      } else {
        const _funds = jurisdictions.find(
          item => item.reporting_member_state === reportingMemberState
        ).funds;
        setSelectedJurisdictions(prev => [
          ...prev,
          { reportingMemberState, pdf: false, xml: false, funds: _funds.map(item => item.id) }
        ]);
      }
    };

    const onSelectPDF = reportingMemberState => {
      setSelectedJurisdictions(prev =>
        prev.map(item =>
          item.reportingMemberState === reportingMemberState ? { ...item, pdf: !item.pdf } : item
        )
      );
    };

    const onSelectXML = reportingMemberState => {
      setSelectedJurisdictions(prev =>
        prev.map(item =>
          item.reportingMemberState === reportingMemberState ? { ...item, xml: !item.xml } : item
        )
      );
    };

    const onSelectAllXML = () => {
      const selectedXMLs = selectedJurisdictions.filter(item => item.xml);
      if (selectedXMLs.length !== selectedJurisdictions.length) {
        setSelectedJurisdictions(prev => prev.map(item => ({ ...item, xml: true })));
      } else {
        setSelectedJurisdictions(prev => prev.map(item => ({ ...item, xml: false })));
      }
    };

    const onSelectAllPDF = () => {
      const selectedPDFs = selectedJurisdictions.filter(item => item.pdf);
      if (selectedPDFs.length !== selectedJurisdictions.length) {
        setSelectedJurisdictions(prev => prev.map(item => ({ ...item, pdf: true })));
      } else {
        setSelectedJurisdictions(prev => prev.map(item => ({ ...item, pdf: false })));
      }
    };

    const onSelectFunds = reportingMemberState => {
      setAction({
        type: ACTION_TYPES.SELECT_FUND,
        reportingMemberState
      });
    };

    const onSaveFunds = (fundIds, reportingMemberState) => {
      const _selectedJurisdictions = cloneDeep(selectedJurisdictions);

      _selectedJurisdictions.forEach(item => {
        if (item.reportingMemberState === reportingMemberState) {
          item.funds = fundIds;
        }
      });

      setSelectedJurisdictions(_selectedJurisdictions);

      setAction({
        type: ACTION_TYPES.JURISDICTIONS
      });
    };

    const onBack = () => {
      setAction({
        type: ACTION_TYPES.JURISDICTIONS
      });
    };

    const selectedJurisdiction = useMemo(() => {
      if (action.type === ACTION_TYPES.SELECT_FUND)
        return jurisdictions.find(
          item => item.reporting_member_state === action.reportingMemberState
        );
      return {};
    }, [action]);

    const onSelectCountry = value => {
      if (expandedCountryCodes.includes(value)) {
        setExpandedCountryCodes(prev => prev.filter(item => item !== value));
      } else {
        setExpandedCountryCodes(prev => [...prev, value]);
      }
    };

    const currentJurisdictionFunds = useMemo(() => {
      return jurisdictions?.find(
        item => item.reporting_member_state === action.reportingMemberState
      )?.funds;
    }, [action, jurisdictions]);

    return (
      <Drawer
        id={id ?? 'export-drawer'}
        title={
          <DrawerTitle
            action={action}
            selectedJurisdiction={selectedJurisdiction}
            onBack={onBack}
          />
        }
        opened={commonStore.isOpenExport}
        onClose={onClose}
        size="lg"
        closeOnClickOutside
        overlayProps={{
          backgroundOpacity: 0.35,
          blur: 3
        }}
        position="right"
      >
        {hasWarning && !showSenderIdNotice && (
          <Alert
            id="export-drawer-warning-alert"
            p="12px"
            variant="outline"
            color="orange"
            title="Warning"
            icon={<TbInfoCircle size={16} />}
            styles={{
              body: {
                gap: '8px'
              }
            }}
          >
            Some funds contain errors and are excluded from export. Fix errors to export all funds.
          </Alert>
        )}
        {showSenderIdNotice && (
          <Alert
            id="export-drawer-sender-id-alert"
            p="12px"
            variant="outline"
            color="orange"
            title="Warning"
            icon={<TbInfoCircle size={16} />}
            styles={{
              body: {
                gap: '8px'
              }
            }}
          >
            Certain countries require specifying a Sender ID. Please include these in the inputs
            below.
          </Alert>
        )}

        <Box id="export-drawer-content" mt="32px">
          {action.type === ACTION_TYPES.JURISDICTIONS && (
            <Jurisdictions
              onSelectJurisdiction={onSelectJurisdiction}
              onSelectPDF={onSelectPDF}
              onSelectXML={onSelectXML}
              selectedJurisdictions={selectedJurisdictions}
              jurisdictions={jurisdictions}
              onSelectAllJurisdictions={onSelectAllJurisdictions}
              onSelectAllXML={onSelectAllXML}
              onSelectAllPDF={onSelectAllPDF}
              onSelectFunds={onSelectFunds}
            />
          )}
          {action.type === ACTION_TYPES.SELECT_FUND && (
            <Funds
              funds={currentJurisdictionFunds}
              reportingMemberState={action.reportingMemberState}
              selectedJurisdictions={selectedJurisdictions}
              onSaveFunds={onSaveFunds}
            />
          )}

          {action.type === ACTION_TYPES.JURISDICTIONS && (
            <JurisdictionsActions
              isMergeAll={isMergeAll}
              setIsMergeAll={setIsMergeAll}
              isLoading={isLoading}
              isDisabled={selectedJurisdictions.length === 0}
              onExportClick={onSkip}
            />
          )}

          {action.type === ACTION_TYPES.ADDITIONAL_INFORMATION && (
            <AdditionalInformationForm
              control={control}
              handleSubmit={handleSubmit}
              onSubmit={onSubmit}
              jurisdictionsIds={jurisdictionsIds}
              expandedCountryCodes={expandedCountryCodes}
              onSelectCountry={onSelectCountry}
              reportingInstitutions={reportingInstitutions}
              typeOfIds={typeOfIds}
              exportWithoutEntries={exportWithoutEntries}
              setExportWithoutEntries={setExportWithoutEntries}
              isDisabledExport={isDisabledExport}
              isLoading={isLoading}
            />
          )}
        </Box>
      </Drawer>
    );
  }
);
